import { useEffect, useMemo, useRef, useState } from "react";
import { useExternalDataStore } from "../../state/external-data-store";

type MediaBrowserModalProps = {
  isOpen: boolean;
  instanceId: string;
  onClose: () => void;
  onPick: (url: string) => void;
  allowUpload?: boolean;
  uploadContext?: string;
};

function splitPath(path: string): string[] {
  const clean = (path ?? "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/\/+/g, "/")
    .replace(/^\/|\/$/g, "");
  return clean ? clean.split("/") : [];
}

function joinParts(parts: string[]): string {
  return parts.filter(Boolean).join("/");
}

function getScopedPrefixParts(path: string): string[] {
  const parts = splitPath(path);
  if (parts.length < 2) return [];
  const lower = parts.map((part) => part.toLowerCase());

  // Group-scoped media can come as:
  // - groups/{groupName}/...
  // - images/groups/{groupName}/...
  if (lower[0] === "groups" && parts[1]) {
    return parts.slice(0, 2);
  }
  if (lower[0] === "images" && lower[1] === "groups" && parts[2]) {
    return parts.slice(0, 3);
  }
  // Shared media can come as:
  // - images/{folder}/...
  //   We keep the folder (e.g. "shared") as the user-facing root label.
  if (lower[0] === "images" && lower[1] && lower[1] !== "groups") {
    return parts.slice(0, 2);
  }
  return [];
}

function isPathWithinRoot(candidatePath: string, rootPath: string): boolean {
  const rootParts = splitPath(rootPath);
  if (rootParts.length === 0) return true;
  const candidateParts = splitPath(candidatePath);
  if (candidateParts.length < rootParts.length) return false;
  for (let idx = 0; idx < rootParts.length; idx += 1) {
    if (candidateParts[idx] !== rootParts[idx]) return false;
  }
  return true;
}

function toDisplayPath(fullPath: string, rootPath: string): string {
  const cleanFull = joinParts(splitPath(fullPath));
  const cleanRoot = joinParts(getScopedPrefixParts(rootPath));
  const isScoped = cleanRoot !== "";

  if (!isScoped || cleanFull === "") return cleanFull;
  if (cleanRoot === "") return cleanFull;
  if (cleanFull === cleanRoot) return "";
  if (cleanFull.startsWith(cleanRoot + "/")) {
    return cleanFull.slice(cleanRoot.length + 1);
  }
  return cleanFull;
}

export function MediaBrowserModal(props: MediaBrowserModalProps) {
  const {
    isOpen,
    instanceId,
    onClose,
    onPick,
    allowUpload = true,
    uploadContext,
  } = props;

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [rootPath, setRootPath] = useState<string>("");
  const [isSharedAlbumsView, setIsSharedAlbumsView] = useState(false);
  const [activeAlbumId, setActiveAlbumId] = useState<string>("");
  const rootPathInitRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchRowRef = useRef<HTMLDivElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const albumBrowserInstanceId = useMemo(() => `${instanceId}__albums`, [instanceId]);

  const mediaBrowserState = useExternalDataStore(
    (state) => state.mediaBrowser[instanceId]
  );
  const albumsState = useExternalDataStore(
    (state) => state.albumsList[albumBrowserInstanceId]
  );
  const albumDetailState = useExternalDataStore(
    (state) => state.albumDetail[albumBrowserInstanceId]
  );
  const setMediaBrowserPath = useExternalDataStore(
    (state) => state.setMediaBrowserPath
  );
  const setMediaBrowserQuery = useExternalDataStore(
    (state) => state.setMediaBrowserQuery
  );
  const setMediaBrowserDebouncedQuery = useExternalDataStore(
    (state) => state.setMediaBrowserDebouncedQuery
  );
  const listMediaAction = useExternalDataStore((state) => state.listMedia);
  const uploadMediaAction = useExternalDataStore((state) => state.uploadMedia);
  const listAlbumsAction = useExternalDataStore((state) => state.listAlbums);
  const getAlbumAction = useExternalDataStore((state) => state.getAlbum);
  const resetAlbumsAction = useExternalDataStore((state) => state.resetAlbums);

  const path = mediaBrowserState?.path ?? "";
  const query = mediaBrowserState?.query ?? "";
  const debouncedQuery = mediaBrowserState?.debouncedQuery ?? "";
  const folders = mediaBrowserState?.folders ?? [];
  const files = mediaBrowserState?.files ?? [];
  const isLoading = mediaBrowserState?.isLoading ?? false;
  const listError = mediaBrowserState?.listError ?? null;
  const isUploading = mediaBrowserState?.isUploading ?? false;
  const uploadError = mediaBrowserState?.uploadError ?? null;
  const albums = albumsState?.items ?? [];
  const albumDetail = albumDetailState?.album ?? null;
  const albumPhotos = albumDetailState?.photos ?? [];
  const stableQuery = useMemo(() => debouncedQuery.trim(), [debouncedQuery]);
  const sharedAlbums = useMemo(() => {
    return albums.filter((album: any) => {
      const rawGroupId = album?.C_com_group_item_id;
      const groupId =
        typeof rawGroupId === "number"
          ? rawGroupId
          : Number.parseInt(String(rawGroupId ?? "0"), 10);
      const groupName = String(album?.C_com_group_item_name ?? "").trim();
      return (!Number.isFinite(groupId) || groupId <= 0) && groupName === "";
    });
  }, [albums]);
  const filteredSharedAlbums = useMemo(() => {
    const q = stableQuery.toLowerCase();
    if (!q) return sharedAlbums;
    return sharedAlbums.filter((album: any) => {
      const name = String(album?.name ?? album?.item_name ?? "").toLowerCase();
      const groupName = String(album?.C_com_group_item_name ?? "").toLowerCase();
      return name.includes(q) || groupName.includes(q);
    });
  }, [sharedAlbums, stableQuery]);
  const filteredAlbumPhotos = useMemo(() => {
    const q = stableQuery.toLowerCase();
    if (!q) return albumPhotos;
    return albumPhotos.filter((photo: any) => {
      const name = String(photo?.item_name ?? "").toLowerCase();
      const filename = String(photo?.attachment_filename ?? "").toLowerCase();
      return name.includes(q) || filename.includes(q);
    });
  }, [albumPhotos, stableQuery]);
  const activeAlbumName = useMemo(() => {
    const activeAlbum = sharedAlbums.find(
      (album: any) => String(album?.id ?? "") === activeAlbumId
    );
    return String(activeAlbum?.name ?? activeAlbum?.item_name ?? "").trim();
  }, [activeAlbumId, sharedAlbums]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setMediaBrowserDebouncedQuery(instanceId, query.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query, instanceId, setMediaBrowserDebouncedQuery]);
  const breadcrumbParts = useMemo(() => splitPath(path), [path]);
  const scopedRootPath = useMemo(() => {
    const fromRoot = getScopedPrefixParts(rootPath);
    if (fromRoot.length > 0) return joinParts(fromRoot);
    const fromPath = getScopedPrefixParts(path);
    if (fromPath.length > 0) return joinParts(fromPath);
    return "";
  }, [rootPath, path]);
  const effectiveRootPath = scopedRootPath || rootPath;
  const scopedPrefixParts = useMemo(() => {
    return splitPath(scopedRootPath);
  }, [scopedRootPath]);
  const rootLabel = useMemo(() => {
    const rootParts = splitPath(effectiveRootPath);
    const lower = rootParts.map((part) => part.toLowerCase());
    if (lower[0] === "images" && lower[1] && lower[1] !== "groups") {
      return rootParts[1];
    }
    return "Images";
  }, [effectiveRootPath]);
  const hiddenPrefixLen = useMemo(() => {
    if (!scopedPrefixParts.length) return 0;
    const currentParts = splitPath(path);
    if (currentParts.length < scopedPrefixParts.length) return 0;
    for (let idx = 0; idx < scopedPrefixParts.length; idx += 1) {
      if (currentParts[idx] !== scopedPrefixParts[idx]) return 0;
    }
    return scopedPrefixParts.length;
  }, [scopedPrefixParts, path]);
  const visibleBreadcrumbParts = useMemo(
    () => breadcrumbParts.slice(hiddenPrefixLen),
    [breadcrumbParts, hiddenPrefixLen]
  );
  const visiblePathLabel = useMemo(() => {
    if (!path) return "/";
    if (visibleBreadcrumbParts.length === 0) return rootLabel;
    return `${rootLabel}/${visibleBreadcrumbParts.join("/")}`;
  }, [path, rootLabel, visibleBreadcrumbParts]);
  const canNavigateUp = useMemo(() => {
    if (!path) return false;
    if (!effectiveRootPath) return true;
    const pathParts = splitPath(path);
    const rootParts = splitPath(effectiveRootPath);
    return (
      isPathWithinRoot(path, effectiveRootPath) &&
      pathParts.length > rootParts.length
    );
  }, [path, effectiveRootPath]);
  const parentPath = useMemo(() => {
    if (!canNavigateUp) return "";
    const parts = splitPath(path);
    return joinParts(parts.slice(0, -1));
  }, [canNavigateUp, path]);
  const parentFolderName = useMemo(() => {
    if (!parentPath) return "";
    if (effectiveRootPath && parentPath === effectiveRootPath) return rootLabel;
    const parts = splitPath(parentPath);
    return parts[parts.length - 1] ?? "";
  }, [parentPath, effectiveRootPath, rootLabel]);
  const isAtGroupRoot = useMemo(() => {
    if (!effectiveRootPath || path !== effectiveRootPath) return false;
    const rootParts = splitPath(effectiveRootPath);
    const lower = rootParts.map((part) => part.toLowerCase());
    return (
      (lower[0] === "groups" && Boolean(rootParts[1])) ||
      (lower[0] === "images" &&
        lower[1] === "groups" &&
        Boolean(rootParts[2]))
    );
  }, [effectiveRootPath, path]);
  useEffect(() => {
    if (!isOpen || isSharedAlbumsView) return;
    void listMediaAction(instanceId, path, stableQuery);
  }, [isOpen, isSharedAlbumsView, path, stableQuery, instanceId, listMediaAction]);

  useEffect(() => {
    if (!isOpen || !isSharedAlbumsView) return;
    void listAlbumsAction(albumBrowserInstanceId, 200, {
      includeGlobalAlbums: true,
    });
  }, [isOpen, isSharedAlbumsView, albumBrowserInstanceId, listAlbumsAction]);

  useEffect(() => {
    if (!isOpen) return;
    const el = searchInputRef.current;
    const row = searchRowRef.current;
    if (!el || !row) return;

    // Force visibility in contexts where external CSS hides search inputs.
    el.style.setProperty("display", "block", "important");
    el.style.setProperty("visibility", "visible", "important");
    el.style.setProperty("opacity", "1", "important");
    row.style.setProperty("display", "flex", "important");
    row.style.setProperty("visibility", "visible", "important");
    row.style.setProperty("opacity", "1", "important");
  }, [isOpen, pendingFile]);

  useEffect(() => {
    if (isOpen) return;
    setRootPath("");
    setIsSharedAlbumsView(false);
    setActiveAlbumId("");
    rootPathInitRef.current = false;
    resetAlbumsAction(albumBrowserInstanceId);
  }, [isOpen, albumBrowserInstanceId, resetAlbumsAction]);

  useEffect(() => {
    if (isSharedAlbumsView) return;
    if (!isOpen) return;
    if (rootPathInitRef.current) return;
    if (isLoading || listError) return;
    rootPathInitRef.current = true;
    setRootPath(path);
  }, [isSharedAlbumsView, isOpen, isLoading, listError, path]);

  useEffect(() => {
    if (!isOpen || !isSharedAlbumsView) return;
    if (filteredSharedAlbums.length === 0) {
      setActiveAlbumId("");
      return;
    }
    setActiveAlbumId((current) => {
      if (
        current &&
        filteredSharedAlbums.some((album: any) => String(album?.id ?? "") === current)
      ) {
        return current;
      }
      return String(filteredSharedAlbums[0]?.id ?? "");
    });
  }, [isOpen, isSharedAlbumsView, filteredSharedAlbums]);

  useEffect(() => {
    if (!isOpen || !isSharedAlbumsView || !activeAlbumId) return;
    void getAlbumAction(albumBrowserInstanceId, activeAlbumId, {
      includeGlobalAlbums: true,
    });
  }, [isOpen, isSharedAlbumsView, activeAlbumId, albumBrowserInstanceId, getAlbumAction]);

  useEffect(() => {
    if (!isOpen) return;
    if (isSharedAlbumsView && !isAtGroupRoot) {
      setIsSharedAlbumsView(false);
      setActiveAlbumId("");
    }
  }, [isOpen, isSharedAlbumsView, isAtGroupRoot]);

  useEffect(() => {
    if (!pendingFile || !isOpen) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
        setImageDimensions(null);
      }
    }
  }, [pendingFile, isOpen, previewUrl]);

  function handleFileSelect(file: File) {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPendingFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
    };
    img.src = objectUrl;
  }

  function clearPendingUpload() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPendingFile(null);
    setPreviewUrl("");
    setImageDimensions(null);
  }

  async function handleUpload() {
    if (!pendingFile) return;
    try {
      const url = await uploadMediaAction(
        instanceId,
        pendingFile,
        path,
        uploadContext
      );
      onPick(url);
      clearPendingUpload();
      void listMediaAction(instanceId, path, stableQuery);
    } catch {
      // Error state is handled in the external data store.
    }
  }

  function handlePick(url: string) {
    const clean = String(url ?? "").trim();
    if (!clean) return;
    onPick(clean);
    clearPendingUpload();
  }

  function closeModal() {
    clearPendingUpload();
    onClose();
  }

  if (!isOpen) return null;

  return (
    <>
      <div
        className="modal fade show"
        role="dialog"
        aria-modal="true"
        style={{ display: "block", zIndex: 20000 }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex flex-column w-100 gap-2">
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <h5 className="modal-title mb-0">
                    {pendingFile ? "Confirm upload" : "Select an image"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close me-0"
                    aria-label="Close"
                    style={{ boxSizing: "content-box" }}
                    onClick={closeModal}
                  />
                </div>

                <div className="d-flex flex-column gap-2">
                  <div
                    ref={searchRowRef}
                    className="d-flex align-items-center gap-2 w-100"
                  >
                    <input
                      ref={searchInputRef}
                      type="search"
                      placeholder={isSharedAlbumsView ? "Search shared albums or photos..." : "Search..."}
                      value={query}
                      onChange={(e) =>
                        setMediaBrowserQuery(instanceId, e.target.value)
                      }
                      className="form-control form-control-sm w-100 w-md-auto"
                    />
                  </div>
                  {!pendingFile && !isSharedAlbumsView && (
                    <div className="d-flex align-items-baseline gap-2 small" style={{ minHeight: "1.5em" }}>
                      <span className="fw-semibold text-nowrap">Current path:</span>
                      <nav aria-label="breadcrumb" className="overflow-hidden">
                        <ol className="breadcrumb mb-0 small flex-nowrap" style={{ lineHeight: "inherit" }}>
                          {/* Root breadcrumb item */}
                          <li
                            className={[
                              "breadcrumb-item",
                              visibleBreadcrumbParts.length === 0 ? "active" : "",
                            ].join(" ")}
                            aria-current={visibleBreadcrumbParts.length === 0 ? "page" : undefined}
                            style={{ lineHeight: "inherit" }}
                          >
                            {visibleBreadcrumbParts.length > 0 ? (
                              <button
                                type="button"
                                className="border-0 bg-transparent p-0 text-primary text-decoration-underline small"
                                style={{ font: "inherit", lineHeight: "inherit" }}
                                onClick={() =>
                                  setMediaBrowserPath(instanceId, effectiveRootPath)
                                }
                              >
                                {rootLabel}
                              </button>
                            ) : (
                              rootLabel
                            )}
                          </li>
                          {/* Remaining path parts */}
                          {breadcrumbParts
                            .map((part, idx) => ({ part, idx }))
                            .filter(({ idx }) => idx >= hiddenPrefixLen)
                            .map(({ part, idx }) => {
                              const to = joinParts(
                                breadcrumbParts.slice(0, idx + 1)
                              );
                              const isLast = idx === breadcrumbParts.length - 1;
                              const canNavigateTo =
                                !isLast && isPathWithinRoot(to, effectiveRootPath);
                              return (
                                <li
                                  key={`${to}-${idx}`}
                                  className={[
                                    "breadcrumb-item",
                                    isLast ? "active" : "",
                                  ].join(" ")}
                                  aria-current={isLast ? "page" : undefined}
                                  style={{ lineHeight: "inherit" }}
                                >
                                  {canNavigateTo ? (
                                    <button
                                      type="button"
                                      className="border-0 bg-transparent p-0 text-primary text-decoration-underline small"
                                      style={{ font: "inherit", lineHeight: "inherit" }}
                                      onClick={() =>
                                        setMediaBrowserPath(instanceId, to)
                                      }
                                    >
                                      {part}
                                    </button>
                                  ) : (
                                    part
                                  )}
                                </li>
                              );
                            })}
                        </ol>
                      </nav>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-body">
              {pendingFile ? (
                <div>
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <div
                        className="border rounded bg-light ratio ratio-1x1 w-100"
                        style={{ maxHeight: "200px", maxWidth: "200px" }}
                      >
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="object-fit-contain top-50 start-50 translate-middle img-fluid"
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center w-100 h-100">
                            <span className="text-body-secondary small">
                              Loading preview...
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex flex-column gap-3">
                        <div>
                          <div className="small text-body-secondary">File</div>
                          <div className="fw-semibold">{pendingFile.name}</div>
                          <label className="btn btn-sm btn-outline-primary mt-2 mb-0">
                            Change file
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              disabled={isUploading}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.currentTarget.value = "";
                                if (f) handleFileSelect(f);
                              }}
                            />
                          </label>
                        </div>
                        {imageDimensions ? (
                          <div>
                            <div className="small text-body-secondary">
                              Dimensions
                            </div>
                            <div>
                              {imageDimensions.width} × {imageDimensions.height}{" "}
                              px
                            </div>
                          </div>
                        ) : null}
                        <div>
                          <div className="small text-body-secondary mb-1">
                            Upload to folder
                          </div>
                          <div
                            className="form-control form-control-sm bg-light"
                            style={{ cursor: "not-allowed" }}
                          >
                            {visiblePathLabel}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {uploadError ? (
                    <div
                      className="alert alert-danger py-2 px-3 small mb-3"
                      role="alert"
                    >
                      <div className="fw-semibold">Upload failed.</div>
                      <div className="text-body-secondary">
                        Please try again in a moment.
                      </div>
                    </div>
                  ) : null}

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={clearPendingUpload}
                      disabled={isUploading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleUpload}
                      disabled={isUploading}
                    >
                      {isUploading ? "Uploading..." : "Upload"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {!isSharedAlbumsView && isLoading ? (
                    <div className="d-flex align-items-center gap-2 small text-body-secondary">
                      <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                      />
                      Loading...
                    </div>
                  ) : null}
                  {!isSharedAlbumsView && !isLoading && listError ? (
                    <div
                      className="alert alert-warning py-2 px-3 small mb-2"
                      role="alert"
                    >
                      <div className="fw-semibold">
                        We could not load the image browser.
                      </div>
                      <div className="text-body-secondary">
                        Please try again in a moment.
                      </div>
                    </div>
                  ) : null}

                  <div className="row g-3">
                    {isSharedAlbumsView ? (
                      <>
                        <div className="col-4">
                          <div className="d-flex align-items-center mb-2" style={{ minHeight: "32px" }}>
                            <div className="small fw-semibold">Folders</div>
                          </div>
                          <div className="list-group">
                            <button
                              type="button"
                              className="list-group-item list-group-item-action"
                              onClick={() => {
                                setIsSharedAlbumsView(false);
                                setActiveAlbumId("");
                              }}
                              title="Go to parent folder"
                            >
                              <span className="d-inline-flex align-items-center gap-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 640 640"
                                  className="me-1 flex-shrink-0"
                                  width="1em"
                                  height="1em"
                                  aria-hidden="true"
                                >
                                  <path
                                    fill="currentColor"
                                    d="M576 448C576 483.3 547.3 512 512 512L128 512C92.7 512 64 483.3 64 448L64 160C64 124.7 92.7 96 128 96L266.7 96C280.5 96 294 100.5 305.1 108.8L343.5 137.6C349 141.8 355.8 144 362.7 144L512 144C547.3 144 576 172.7 576 208L576 448zM337 231C327.6 221.6 312.4 221.6 303.1 231L231.1 303C221.7 312.4 221.7 327.6 231.1 336.9C240.5 346.2 255.7 346.3 265 336.9L296 305.9L296 408C296 421.3 306.7 432 320 432C333.3 432 344 421.3 344 408L344 305.9L375 336.9C384.4 346.3 399.6 346.3 408.9 336.9C418.2 327.5 418.3 312.3 408.9 303L336.9 231z"
                                  />
                                </svg>
                                {rootLabel}
                              </span>
                            </button>
                            {albumsState?.isLoading ? (
                              <div className="small text-body-secondary">Loading albums...</div>
                            ) : filteredSharedAlbums.length === 0 ? (
                              <div className="small text-body-secondary">No shared albums</div>
                            ) : (
                              filteredSharedAlbums.map((album: any) => {
                                const albumId = String(album?.id ?? "");
                                const isActive = albumId === activeAlbumId;
                                return (
                                  <button
                                    key={albumId}
                                    type="button"
                                    className={[
                                      "list-group-item",
                                      "list-group-item-action",
                                      "d-flex",
                                      "align-items-center",
                                      "justify-content-between",
                                      "gap-2",
                                      isActive ? "active" : "",
                                    ].join(" ")}
                                    onClick={() => setActiveAlbumId(albumId)}
                                  >
                                    <span className="text-truncate" title={String(album?.name ?? album?.item_name ?? "Untitled album")}>
                                      {String(album?.name ?? album?.item_name ?? "Untitled album")}
                                    </span>
                                    <span className={isActive ? "text-white-50" : "text-body-secondary"}>›</span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>

                        <div className="col-8">
                          <div className="d-flex align-items-center justify-content-between mb-2" style={{ minHeight: "32px" }}>
                            <div className="small fw-semibold">Images</div>
                          </div>
                          {albumDetailState?.error ? (
                            <div className="alert alert-warning py-2 px-3 small mb-2" role="alert">
                              <div className="fw-semibold">We could not load the album.</div>
                              <div className="text-body-secondary">Please try again in a moment.</div>
                            </div>
                          ) : null}
                          {!activeAlbumId ? (
                            <div className="small text-body-secondary">Select an album</div>
                          ) : albumDetailState?.isLoading && !albumDetail ? (
                            <div className="small text-body-secondary">Loading album photos...</div>
                          ) : filteredAlbumPhotos.length === 0 ? (
                            <div className="small text-body-secondary">No photos</div>
                          ) : (
                            <div className="list-group">
                              {filteredAlbumPhotos.map((photo: any) => {
                                const photoUrl = String(photo?.url ?? "").trim();
                                const photoName = String(
                                  photo?.item_name ?? photo?.attachment_filename ?? "Album photo"
                                ).trim();
                                return (
                                  <button
                                    key={String(photo?.id ?? photoUrl)}
                                    type="button"
                                    className="list-group-item list-group-item-action d-flex align-items-center gap-2"
                                    onClick={() => handlePick(photoUrl)}
                                    disabled={!photoUrl}
                                  >
                                    <div
                                      className="border rounded bg-light ratio ratio-1x1 flex-shrink-0 w-25"
                                      style={{ maxWidth: "100%" }}
                                    >
                                      {photoUrl ? (
                                        <img
                                          src={photoUrl}
                                          alt=""
                                          className="object-fit-contain top-50 start-50 translate-middle img-fluid"
                                        />
                                      ) : null}
                                    </div>
                                    <div className="flex-grow-1 overflow-hidden">
                                      <div className="text-truncate" title={photoName}>
                                        {photoName}
                                      </div>
                                      <div
                                        className="small text-body-secondary text-truncate"
                                        title={String(photo?.attachment_filename ?? activeAlbumName ?? "Album photo")}
                                      >
                                        {String(photo?.attachment_filename ?? activeAlbumName ?? "Album photo")}
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="col-4">
                          <div className="d-flex align-items-center mb-2" style={{ minHeight: "32px" }}>
                            <div className="small fw-semibold">Folders</div>
                          </div>
                          <div className="list-group">
                            {canNavigateUp ? (
                              <button
                                type="button"
                                className="list-group-item list-group-item-action"
                                onClick={() => {
                                  if (
                                    effectiveRootPath &&
                                    !isPathWithinRoot(parentPath, effectiveRootPath)
                                  ) {
                                    return;
                                  }
                                  setMediaBrowserPath(instanceId, parentPath);
                                }}
                                title="Go to parent folder"
                              >
                                <span className="d-inline-flex align-items-center gap-2">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 640 640"
                                    className="me-1 flex-shrink-0"
                                    width="1em"
                                    height="1em"
                                    aria-hidden="true"
                                  >
                                    <path
                                      fill="currentColor"
                                      d="M576 448C576 483.3 547.3 512 512 512L128 512C92.7 512 64 483.3 64 448L64 160C64 124.7 92.7 96 128 96L266.7 96C280.5 96 294 100.5 305.1 108.8L343.5 137.6C349 141.8 355.8 144 362.7 144L512 144C547.3 144 576 172.7 576 208L576 448zM337 231C327.6 221.6 312.4 221.6 303.1 231L231.1 303C221.7 312.4 221.7 327.6 231.1 336.9C240.5 346.2 255.7 346.3 265 336.9L296 305.9L296 408C296 421.3 306.7 432 320 432C333.3 432 344 421.3 344 408L344 305.9L375 336.9C384.4 346.3 399.6 346.3 408.9 336.9C418.2 327.5 418.3 312.3 408.9 303L336.9 231z"
                                    />
                                  </svg>
                                  {parentFolderName || rootLabel}
                                </span>
                              </button>
                            ) : null}

                            {folders.length === 0 ? (
                              <div className="small text-body-secondary">
                                No folders
                              </div>
                            ) : (
                              folders.map((f) => (
                                <button
                                  key={f.path}
                                  type="button"
                                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between gap-2"
                                  onClick={() =>
                                    setMediaBrowserPath(instanceId, f.path)
                                  }
                                >
                                  <span className="text-truncate" title={f.name}>
                                    {f.name}
                                  </span>
                                  <span className="text-body-secondary">›</span>
                                </button>
                              ))
                            )}
                            {isAtGroupRoot ? (
                              <>
                                <button
                                  type="button"
                                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between gap-2"
                                  onClick={() => {
                                    setIsSharedAlbumsView(true);
                                    setActiveAlbumId("");
                                  }}
                                >
                                  <span className="text-truncate" title="Shared Albums">
                                    Shared Albums
                                  </span>
                                  <span className="text-body-secondary">›</span>
                                </button>
                              </>
                            ) : null}
                          </div>
                        </div>

                        <div className="col-8">
                          <div className="d-flex align-items-center justify-content-between mb-2" style={{ minHeight: "32px" }}>
                            <div className="small fw-semibold">
                              Images
                            </div>
                            {allowUpload ? (
                              <label className="btn btn-xs btn-primary mb-0 text-nowrap">
                                Upload New Image
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.currentTarget.value = "";
                                    if (f) handleFileSelect(f);
                                  }}
                                />
                              </label>
                            ) : null}
                          </div>
                          {files.length === 0 ? (
                            <div className="small text-body-secondary">
                              No images
                            </div>
                          ) : (
                            <div className="list-group">
                              {files.map((f) => (
                                <button
                                  key={f.path}
                                  type="button"
                                  className="list-group-item list-group-item-action d-flex align-items-center gap-2"
                                  onClick={() => handlePick(f.url)}
                                >
                                  <div
                                    className="border rounded bg-light ratio ratio-1x1 flex-shrink-0 w-25"
                                    style={{ maxWidth: "100%" }}
                                  >
                                    <img
                                      src={f.url}
                                      alt=""
                                      className="object-fit-contain top-50 start-50 translate-middle img-fluid"
                                    />
                                  </div>
                                  <div className="flex-grow-1 overflow-hidden">
                                    <div className="text-truncate" title={f.name}>
                                      {f.name}
                                    </div>
                                    <div
                                      className="small text-body-secondary text-truncate"
                                      title={toDisplayPath(f.path, effectiveRootPath) || rootLabel}
                                    >
                                      {toDisplayPath(f.path, effectiveRootPath) || rootLabel}
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 19999 }}
        onMouseDown={closeModal}
      />
    </>
  );
}

